import { ClockIcon, CpuIcon } from "@primer/octicons-react";
import { rp } from "../api/client";
import { Link } from "react-router-dom";
import type { QueueStats } from "../api/types";
import { Empty, Sha, TimeAgo, Who } from "../components/common";
import { safeJson } from "../components/format";
import { Subhead } from "../components/SettingsLayout";
import { Badge, Counter } from "../components/ui/badge";
import { Card, CardHeader } from "../components/ui/card";
import { List, ListItem, ListItemMeta, ListItemTitle } from "../components/ui/list";

const STALL_MS = 120_000;
type Row = QueueStats["queued"][number] | QueueStats["claimed"][number];
type Payload = { job?: string; commit?: string; change_id?: string; attempt?: number };

/** All of one commit's waiting and running jobs, shown as a single row. */
interface Group {
  key: string;
  change: string;
  commit?: string;
  running: Array<{ job: string; by: string }>;
  waiting: string[];
  oldest: number;
  firstId: number;
  retried: boolean;
}

/** Collapse jobs into one group per change and commit, so a ten-job matrix is one line, not ten. */
function group(queued: QueueStats["queued"], claimed: QueueStats["claimed"]): Group[] {
  const groups = new Map<string, Group>();
  const add = (j: Row) => {
    const p = (safeJson(j.payload) ?? {}) as Payload;
    const key = `${p.change_id ?? ""}@${p.commit ?? j.id}`;
    let g = groups.get(key);
    if (!g) groups.set(key, (g = { key, change: p.change_id ?? "", commit: p.commit, running: [], waiting: [], oldest: Infinity, firstId: j.id, retried: false }));
    g.firstId = Math.min(g.firstId, j.id);
    g.retried ||= (p.attempt ?? 1) > 1;
    const name = p.job ?? "job";
    if ("claimed_by" in j) g.running.push({ job: name, by: j.claimed_by });
    else {
      g.waiting.push(name);
      g.oldest = Math.min(g.oldest, j.created);
    }
  };
  claimed.forEach(add);
  queued.forEach(add);
  return [...groups.values()].sort((a, b) => a.firstId - b.firstId);
}

/** "build ×3, lint" from a list of names with repeats. */
function names(list: string[], max = 3): string {
  const n = new Map<string, number>();
  for (const x of list) n.set(x, (n.get(x) ?? 0) + 1);
  const parts = [...n].map(([k, c]) => (c > 1 ? `${k} ×${c}` : k));
  return parts.length > max ? `${parts.slice(0, max).join(", ")} +${parts.length - max} more` : parts.join(", ");
}

const BACKGROUND: Record<string, string> = { mirror: "pushing to the remote", remote: "syncing the remote", import: "importing" };

/** The repo's job queue, compacted: one row per commit, background work folded into a line, nothing at all when idle. */
export function RepoQueue({ repo, q }: { repo: string; q: QueueStats }) {
  const ci = (j: Row) => j.kind === "ci";
  const groups = group(q.queued.filter(ci), q.claimed.filter(ci));
  const background = [...q.queued, ...q.claimed].filter((j) => !ci(j));
  const bg = [...new Set(background.map((j) => BACKGROUND[j.kind] ?? j.kind))];
  const runningNow = groups.reduce((n, g) => n + g.running.length, 0);
  const waitingNow = groups.reduce((n, g) => n + g.waiting.length, 0);
  const oldestWait = Math.min(...groups.map((g) => g.oldest));
  const stalled = runningNow === 0 && waitingNow > 0 && Date.now() - oldestWait > STALL_MS;

  return (
    <>
      <Subhead title="Queue">
        {groups.length
          ? `${runningNow} running · ${waitingNow} waiting, across ${groups.length} ${groups.length === 1 ? "commit" : "commits"}`
          : "Nothing is running or waiting."}
        {bg.length > 0 && ` · ${bg.join(", ")}`}
      </Subhead>
      {stalled && (
        <p className="mb-3 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
          Nothing has picked these jobs up for <TimeAgo ms={oldestWait} />. No runner may be online for this repository; ask an owner of the organization to check its Runners settings.
        </p>
      )}
      {groups.length > 0 ? (
        <Card>
          <CardHeader>
            <ClockIcon /> In line <Counter>{groups.length}</Counter>
          </CardHeader>
          <List>
            {groups.map((g, i) => (
              <ListItem key={g.key}>
                <ListItemTitle>
                  <span className="mr-2 text-xs tabular-nums text-muted-foreground">#{i + 1}</span>
                  {g.change.startsWith("verify:") ? (
                    <Badge tone="purple">verification</Badge>
                  ) : g.change ? (
                    <Link className="font-mono text-sm" to={`${rp(repo)}/changes/${g.change}`}>
                      #{g.change.slice(-8)}
                    </Link>
                  ) : null}{" "}
                  <Sha repo={repo} sha={g.commit} n={7} />
                  {g.retried && (
                    <Badge tone="yellow" className="ml-2">
                      retry
                    </Badge>
                  )}
                </ListItemTitle>
                <ListItemMeta className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  {g.running.length > 0 && (
                    <span className="inline-flex items-center gap-1.5">
                      <CpuIcon size={14} /> running {names(g.running.map((r) => r.job))}
                      <Who name={g.running[0].by} size={16} />
                    </span>
                  )}
                  {g.waiting.length > 0 && (
                    <span>
                      waiting {names(g.waiting)} · <TimeAgo ms={g.oldest} />
                    </span>
                  )}
                </ListItemMeta>
              </ListItem>
            ))}
          </List>
        </Card>
      ) : (
        <Card>
          <Empty icon={ClockIcon}>Idle. New jobs appear here as soon as a change is checked.</Empty>
        </Card>
      )}
    </>
  );
}
